import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import UserProfile from "@/pages/SettingsPage";
import RequestTrip from "@/pages/RequestTrip";
import SetPassword from "@/pages/SetPassword";
import AuthLinkError from "@/pages/AuthLinkError";
import NotFound from "@/pages/NotFound";
import { LegalRedirect } from "@/components/ExternalRedirect";
import ErrorPage from "@/pages/ErrorPage";
import WorkingProgress from "@/pages/WorkingProgress";
import ItineraryBuilder from "@/pages/ItineraryBuilder";
import Playground from "@/pages/Playground";
import SharedProposalPage from "@/pages/SharedProposalPage";
import Shared from "@/pages/Shared";
import DownloadedPage from "@/pages/DownloadedPage";
import OfflineViewerPage from "@/pages/OfflineViewerPage";
import ApprovalRoute from "@/components/ApprovalRoute";
import LoginRouteGuard from "@/components/LoginRouteGuard";
import AdminDashboard from "@/pages/AdminDashboard";
import AgencyOverview from "@/pages/AgencyOverview";
import LibraryPage from "@/pages/LibraryPage";
import PaymentsPage from "@/pages/PaymentsPage";
import RequestsPage from "@/pages/RequestsPage";

const AppRouter = () => {
  const { loading } = useAuth();

  // Se è in corso il caricamento dell'autenticazione, mostra la schermata di loading
  if (loading) {
    return <></>;
  }

  return (
    <Routes>
      {/* Redirect root to dashboard */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Protected routes - require authentication and approval */}
      <Route path="/dashboard" element={
        <ApprovalRoute>
          <Dashboard />
        </ApprovalRoute>
      } />

      {/* Le richieste di viaggio hanno una pagina propria: la dashboard resta
          ai soli itinerari. */}
      {/* Il traveler la vede in sola lettura: la RPC gli restituisce solo le sue. */}
      <Route path="/requests" element={
        <ApprovalRoute allowedRoles={['travel_designer', 'travel_advisor_member', 'admin', 'agency', 'traveler']}>
          <RequestsPage />
        </ApprovalRoute>
      } />

      <Route path="/profile" element={
        <ApprovalRoute>
          <UserProfile />
        </ApprovalRoute>
      } />

      <Route path="/itinerary-builder" element={
        <ApprovalRoute>
          <ItineraryBuilder />
        </ApprovalRoute>
      } />

      {/* Redirect /agency and /brand to settings tabs */}
      <Route path="/agency" element={<Navigate to="/profile?tab=agency" replace />} />
      <Route path="/brand" element={<Navigate to="/profile" replace />} />

      <Route path="/admin" element={
        <ApprovalRoute allowedRoles={['admin']}>
          <AdminDashboard />
        </ApprovalRoute>
      } />

      {/* I template sono diventati due tab della dashboard admin. */}
      <Route path="/admin/email-templates" element={<Navigate to="/admin?tab=emails" replace />} />

      {/* Panoramica e Dati sono una pagina sola: la panoramica KPI e' la prima tab. */}
      <Route path="/overview" element={
        <ApprovalRoute allowedRoles={['agency', 'admin', 'travel_designer', 'travel_advisor_member']}>
          <AgencyOverview />
        </ApprovalRoute>
      } />

      {/* Vecchio indirizzo della pagina Dati: i link salvati tengono la tab. */}
      <Route path="/overview/data" element={<OverviewDataRedirect />} />

      <Route path="/playground" element={
        <ApprovalRoute allowedRoles={['travel_designer', 'travel_advisor_member', 'agency', 'admin']}>
          <Playground />
        </ApprovalRoute>
      } />

      <Route path="/shared" element={
        <ApprovalRoute allowedRoles={['travel_designer', 'travel_advisor_member', 'admin', 'agency']}>
          <Shared />
        </ApprovalRoute>
      } />

      <Route path="/library" element={
        <ApprovalRoute allowedRoles={['travel_designer', 'travel_advisor_member', 'admin', 'agency']}>
          <LibraryPage />
        </ApprovalRoute>
      } />

      <Route path="/payments" element={
        <ApprovalRoute>
          <PaymentsPage />
        </ApprovalRoute>
      } />

      {/* Working progress pages */}
      <Route path="/booking" element={
        <ApprovalRoute>
          <WorkingProgress />
        </ApprovalRoute>
      } />


      {/* Redirect old trips route */}
      <Route path="/trips" element={<Navigate to="/dashboard" replace />} />

      {/* Public routes - no authentication required */}
      {/* I documenti legali stanno sul sito: qui restano solo i rimandi, per i
          link vecchi che girano ancora. */}
      <Route path="/terms" element={<LegalRedirect doc="terms" />} />
      <Route path="/privacy" element={<LegalRedirect doc="privacy" />} />
      <Route path="/set-password" element={<SetPassword />} />
      <Route path="/auth-link-error" element={<AuthLinkError />} />
      <Route path="/request-trip" element={<RequestTrip />} />
      <Route path="/shared/:shareToken" element={<SharedProposalPage />} />
      <Route path="/downloaded" element={<DownloadedPage />} />
      <Route path="/downloaded/:id" element={<OfflineViewerPage />} />

      {/* Login route - redirects if already authenticated */}
      <Route path="/login" element={
        <LoginRouteGuard>
          <Login />
        </LoginRouteGuard>
      } />

      {/* 404 - only show when not loading */}
      {/* Fallback errore grave: raggiungibile anche con un redirect esplicito */}
      <Route path="/error" element={<ErrorPage />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

/** /overview/data?tab=x → /overview?tab=x: la pagina Dati ora vive su /overview. */
const OverviewDataRedirect = () => {
  const { search } = useLocation();
  return <Navigate to={`/overview${search}`} replace />;
};

export default AppRouter;
